import { ChevronDown, ChevronRight } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { StatusDot } from '@/components/ui/badge'
import { healthLevelFor } from '@/features/admin/processing-format'
import type { ProcessingPipelineState } from '@/services/api/adminProcessing'
import { ActiveJobsCard } from './ActiveJobsCard'
import { FailuresCard } from './FailuresCard'
import { HealthCard } from './HealthCard'
import { QueueCard } from './QueueCard'
import { StepsCard } from './StepsCard'

export function PipelineSection({
  pipeline,
  rangeLabel,
  collapsed,
  onToggle,
}: {
  pipeline: ProcessingPipelineState
  rangeLabel: string
  collapsed: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation()
  const level = useMemo(() => healthLevelFor(pipeline), [pipeline])
  const dotStatus = level === 'ok' ? 'active' : level === 'warn' ? 'pending' : 'error'

  return (
    <section className="space-y-3">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2 rounded-md border bg-muted/20 px-3 py-2 text-left transition-colors hover:bg-muted/40"
      >
        {collapsed ? (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <StatusDot status={dotStatus} />
        {/* One flex item below md, so the queue name and the stats wrap under the pipeline name
            rather than under the chevron; from md its children sit directly in the header row. */}
        <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-1 md:contents">
          <span className="text-base font-semibold">{pipeline.label}</span>
          <span className="font-mono text-xs text-muted-foreground max-md:min-w-0 max-md:truncate md:ml-2">
            {pipeline.health.queue}
          </span>
          <div className="flex items-center gap-3 text-xs text-muted-foreground max-md:w-full max-md:flex-wrap max-md:gap-y-0.5 md:ml-auto">
            <span>
              {t('admin.processing.pipeline.workers')} {pipeline.health.workers}
            </span>
            {pipeline.queue && (
              <span>
                {t('admin.processing.pipeline.ready')} {pipeline.queue.ready.toLocaleString()}
              </span>
            )}
            {pipeline.has_jobs && pipeline.metrics && (
              <span>
                {t('admin.processing.pipeline.done')} {pipeline.metrics.jobs.done} ·{' '}
                {t('admin.processing.pipeline.failed')} {pipeline.metrics.jobs.failed}
              </span>
            )}
          </div>
        </div>
      </button>

      {!collapsed && (
        <div className="space-y-3 md:pl-4">
          <div className="grid gap-3 lg:grid-cols-2">
            <HealthCard pipeline={pipeline} />
            <QueueCard pipeline={pipeline} />
          </div>

          {pipeline.has_jobs && (
            <>
              <ActiveJobsCard pipeline={pipeline} rangeLabel={rangeLabel} />
              <FailuresCard pipeline={pipeline} rangeLabel={rangeLabel} />
              <StepsCard pipeline={pipeline} rangeLabel={rangeLabel} />
            </>
          )}
        </div>
      )}
    </section>
  )
}
